import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  Alert,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { challengeService, Challenge, ChallengeResponse } from '../../services/challengeService';

const ChallengeList: React.FC = () => {
  const navigate = useNavigate();
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [totalChallenges, setTotalChallenges] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useState({
    skip: 0,
    limit: 10,
    search: '',
    difficulty: undefined as 'Easy' | 'Medium' | 'Hard' | undefined,
    is_active: undefined as boolean | undefined,
  });

  const fetchChallenges = async () => {
    try {
      setLoading(true);
      setError(null);
      const response: ChallengeResponse = await challengeService.listChallenges(params);
      if (response && Array.isArray(response.data)) {
        setChallenges(response.data);
        setTotalChallenges(response.total);
      } else {
        setError('Invalid response format from server');
        setChallenges([]);
        setTotalChallenges(0);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch challenges');
      setChallenges([]);
      setTotalChallenges(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChallenges();
  }, [params]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setParams(prev => ({ ...prev, search: event.target.value, skip: 0 }));
  };

  const handleDifficultyChange = (difficulty: 'Easy' | 'Medium' | 'Hard' | undefined) => {
    setParams(prev => ({ ...prev, difficulty, skip: 0 }));
  };

  const handleStatusChange = (is_active: boolean | undefined) => {
    setParams(prev => ({ ...prev, is_active, skip: 0 }));
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this challenge?')) {
      try {
        await challengeService.deleteChallenge(id);
        fetchChallenges();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete challenge');
      }
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
      await challengeService.toggleChallengeStatus(id);
      fetchChallenges();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle challenge status');
    }
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setParams(prev => ({ ...prev, skip: newPage * prev.limit }));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = parseInt(event.target.value, 10);
    setParams(prev => ({ ...prev, limit: newLimit, skip: 0 }));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Challenges</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/challenges/new')}
        >
          Add Challenge
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
          <Button
            size="small"
            color="inherit"
            onClick={() => fetchChallenges()}
            sx={{ ml: 2 }}
          >
            Retry
          </Button>
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={params.search}
              onChange={handleSearch}
              sx={{ flexGrow: 1 }}
            />
            <Button
              variant={params.difficulty === undefined ? 'contained' : 'outlined'}
              onClick={() => handleDifficultyChange(undefined)}
            >
              All Difficulties
            </Button>
            <Button
              variant={params.difficulty === 'Easy' ? 'contained' : 'outlined'}
              onClick={() => handleDifficultyChange('Easy')}
            >
              Easy
            </Button>
            <Button
              variant={params.difficulty === 'Medium' ? 'contained' : 'outlined'}
              onClick={() => handleDifficultyChange('Medium')}
            >
              Medium
            </Button>
            <Button
              variant={params.difficulty === 'Hard' ? 'contained' : 'outlined'}
              onClick={() => handleDifficultyChange('Hard')}
            >
              Hard
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant={params.is_active === undefined ? 'contained' : 'outlined'}
              onClick={() => handleStatusChange(undefined)}
            >
              All Status
            </Button>
            <Button
              variant={params.is_active === true ? 'contained' : 'outlined'}
              onClick={() => handleStatusChange(true)}
            >
              Active
            </Button>
            <Button
              variant={params.is_active === false ? 'contained' : 'outlined'}
              onClick={() => handleStatusChange(false)}
            >
              Inactive
            </Button>
          </Box>
        </CardContent>
      </Card>

      <TableContainer component={Card}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Difficulty</TableCell>
              <TableCell>Points</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : challenges.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No challenges found
                </TableCell>
              </TableRow>
            ) : (
              challenges.map((challenge) => (
                <TableRow key={challenge.id}>
                  <TableCell>{challenge.title}</TableCell>
                  <TableCell>
                    <Chip
                      label={challenge.difficulty}
                      color={
                        challenge.difficulty === 'Easy'
                          ? 'success'
                          : challenge.difficulty === 'Medium'
                          ? 'warning'
                          : 'error'
                      }
                    />
                  </TableCell>
                  <TableCell>{challenge.points}</TableCell>
                  <TableCell>
                    <Chip
                      label={challenge.is_active ? 'Active' : 'Inactive'}
                      color={challenge.is_active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/admin/challenges/${challenge.id}`)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={challenge.is_active ? 'Deactivate' : 'Activate'}>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleStatus(challenge.id)}
                        >
                          {challenge.is_active ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(challenge.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalChallenges}
          page={params.skip / params.limit}
          onPageChange={handlePageChange}
          rowsPerPage={params.limit}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>
    </Box>
  );
};

export default ChallengeList; 